import {
  List as ListIcon,
  Settings as SettingsIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  Tab,
  Tabs,
  Typography,
} from "@mui/material";
import type React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import {
  createReservationConfig,
  type Fleet,
  getFleetByCity,
  getReservationConfigByCityId,
  type ReservationConfigWithDeposit,
  updateReservationConfig,
} from "../../apis/reservations/apis";
import {
  DEFAULT_RESERVATION_CONFIG,
  minutesToTimeDelta,
} from "../../domain/reservations";
import { useCity } from "../../hooks";
import { ReservationsDisabled } from "./ReservationsDisabled";
import { ReservationsForm } from "./ReservationsForm";
import { ReservationsList } from "./ReservationsList";

interface City {
  id: number;
  name: string;
}

export const ReservationsContainer: React.FC = () => {
  const { selectedCity } = useCity();
  const cityId = selectedCity ? parseInt(selectedCity) : null;
  const citiesList = useSelector(
    (state: any) => state.cities.citiesList
  ) as City[];

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fleet, setFleet] = useState<Fleet | null>(null);
  const [config, setConfig] = useState<ReservationConfigWithDeposit | null>(
    null
  );
  const [currency, setCurrency] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<number | null>(null);
  const [allCitiesSameFleet, setAllCitiesSameFleet] = useState(false);

  const sharedCities = useMemo(() => {
    if (!fleet || !citiesList) return [];
    return citiesList.filter((city) => fleet.city_ids.includes(city.id));
  }, [fleet, citiesList]);

  const fetchData = useCallback(async () => {
    if (!citiesList?.length) {
      setLoading(false);
      setFleet(null);
      setConfig(null);
      setCurrency(null);
      setAllCitiesSameFleet(false);
      return;
    }

    const targetCityId = cityId || citiesList[0].id;

    setLoading(true);
    setError(null);

    try {
      const [fleetData, configData] = await Promise.all([
        getFleetByCity(targetCityId),
        getReservationConfigByCityId(targetCityId),
      ]);

      const allCitiesInFleet = fleetData
        ? citiesList.every((city) => fleetData.city_ids.includes(city.id))
        : false;

      if (!cityId && !allCitiesInFleet) {
        setFleet(null);
        setConfig(null);
        setCurrency(null);
        setAllCitiesSameFleet(false);
        return;
      }

      setFleet(fleetData);
      setConfig(configData);
      setCurrency(configData?.deposit?.currency || null);
      setAllCitiesSameFleet(allCitiesInFleet);
    } catch (e) {
      if (!cityId) {
        setFleet(null);
        setConfig(null);
        setCurrency(null);
        setAllCitiesSameFleet(false);
        return;
      }
      setError("Failed to load reservation configuration");
    } finally {
      setLoading(false);
    }
  }, [cityId, citiesList]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (activeTab === null && !loading) {
      setActiveTab(0);
    }
  }, [loading, activeTab]);

  const handleEnable = async () => {
    if (!fleet) return;

    setLoading(true);
    try {
      const newConfig = await createReservationConfig({
        fleet_id: fleet.id,
        max_future_reservation_start_datetime: minutesToTimeDelta(
          DEFAULT_RESERVATION_CONFIG.reservationDurationMinutes
        ),
        disallow_vehicle_from_being_rented_before_start_datetime:
          minutesToTimeDelta(
            DEFAULT_RESERVATION_CONFIG.reservationDurationMinutes
          ),
        allow_cancel_reservation_before_start_datetime: minutesToTimeDelta(
          DEFAULT_RESERVATION_CONFIG.cancellationWindowMinutes
        ),
        deposit_amount_minor: DEFAULT_RESERVATION_CONFIG.depositAmountMinor,
        no_show_grace_period: minutesToTimeDelta(
          DEFAULT_RESERVATION_CONFIG.noShowGracePeriodMinutes
        ),
      });
      setConfig(newConfig);
    } catch (e) {
      setError("Failed to enable reservations");
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (updatedConfig: ReservationConfigWithDeposit) => {
    try {
      const result = await updateReservationConfig({
        configuration_id: updatedConfig.id,
        max_future_reservation_start_datetime:
          updatedConfig.max_future_reservation_start_datetime,
        disallow_vehicle_from_being_rented_before_start_datetime:
          updatedConfig.disallow_vehicle_from_being_rented_before_start_datetime,
        allow_cancel_reservation_before_start_datetime:
          updatedConfig.allow_cancel_reservation_before_start_datetime,
        deposit_amount_minor: updatedConfig.deposit?.amount_minor,
        no_show_grace_period: updatedConfig.no_show_grace_period,
      });
      setConfig(result);
    } catch (e) {
      throw e;
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          padding: "20px",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "200px",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ padding: "20px" }}>
        <Typography variant="h5" sx={{ marginBottom: "20px", fontWeight: 600 }}>
          Reservations Configuration
        </Typography>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  if (!selectedCity && !allCitiesSameFleet) {
    return (
      <Box sx={{ padding: "20px" }}>
        <Typography variant="h5" sx={{ marginBottom: "20px", fontWeight: 600 }}>
          Reservations
        </Typography>
        <Alert severity="info">
          Please select a specific city to manage reservations.
        </Alert>
      </Box>
    );
  }

  if (!fleet) {
    return (
      <Box sx={{ padding: "20px" }}>
        <Typography variant="h5" sx={{ marginBottom: "20px", fontWeight: 600 }}>
          Reservations
        </Typography>
        <Alert severity="warning">
          Reservations are not available for this city. Please contact support
          if you believe this is an error.
        </Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ padding: "20px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          Reservations
        </Typography>
      </Box>

      {activeTab === 1 && (sharedCities.length > 1 || allCitiesSameFleet) && (
        <Alert severity="info" sx={{ mb: 3 }}>
          <Typography variant="body2">
            This configuration is shared across the following cities:
          </Typography>
          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5, mt: 1 }}>
            {sharedCities.map((city) => (
              <Chip
                key={city.id}
                label={city.name}
                size="small"
                color={city.id === cityId ? "primary" : "default"}
                variant={city.id === cityId ? "filled" : "outlined"}
              />
            ))}
          </Box>
        </Alert>
      )}

      <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
        <Tabs
          value={activeTab ?? 0}
          onChange={(_e, newValue) => setActiveTab(newValue)}
          aria-label="reservations tabs"
        >
          <Tab
            icon={<ListIcon />}
            iconPosition="start"
            label="Reservations"
            sx={{ textTransform: "none" }}
          />
          <Tab
            icon={<SettingsIcon />}
            iconPosition="start"
            label="Configuration"
            sx={{ textTransform: "none" }}
          />
        </Tabs>
      </Box>

      {activeTab === 0 && (
        <ReservationsList fleetId={fleet.id} currency={currency} />
      )}

      {activeTab === 1 && (
        <>
          {!config ? (
            <ReservationsDisabled onEnable={handleEnable} loading={loading} />
          ) : (
            <ReservationsForm
              config={config}
              currency={currency}
              onSave={handleSave}
            />
          )}
        </>
      )}
    </Box>
  );
};
